import { ReactNode, useState, useCallback } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  FileText,
  Users,
  AlertCircle,
  CheckCircle,
  Play,
} from 'lucide-react';

import { Button } from '../ui/button';

import { FileUploadStep } from './FileUploadStep';
import { FundMatchingStep } from './FundMatchingStep';
import { ConflictResolutionStep } from './ConflictResolutionStep';
import { ImportExecutionStep } from './ImportExecutionStep';
import {
  useInvestorImportDeleteSession,
  useInvestorImportUpdateMatches,
  useInvestorImportResolveConflicts,
  useInvestorImportExecute,
} from '@/hooks/react-query/useInvestorImport';

import type {
  ImportValidationResult,
  FundMatchingData,
  ExistingInvestorConflict,
  ImportResult,
} from '../../types/investor-import';

interface WizardStep {
  id: string;
  title: string;
  description: string;
  icon: ReactNode;
  completed: boolean;
  current: boolean;
}

function getStepCircleClass(step: WizardStep) {
  if (step.completed) return 'bg-green-100 border-green-500 text-green-600';
  if (step.current) return 'bg-blue-100 border-blue-500 text-blue-600';
  return 'bg-gray-100 border-gray-300 text-gray-400';
}

function getStepTitleClass(step: WizardStep) {
  if (step.current) return 'text-blue-600';
  if (step.completed) return 'text-green-600';
  return 'text-gray-500';
}

interface InvestorImportWizardProps {
  onClose: () => void;
}

export const InvestorImportWizard = ({
  onClose,
}: InvestorImportWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionId, setSessionId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Step data
  const [validationResult, setValidationResult] =
    useState<ImportValidationResult | null>(null);
  const [fundMatches, setFundMatches] = useState<FundMatchingData[]>([]);
  const [conflicts, setConflicts] = useState<ExistingInvestorConflict[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Mutations
  const { mutate: cleanupSession } = useInvestorImportDeleteSession();
  const updateMatchesMutation = useInvestorImportUpdateMatches();
  const resolveConflictsMutation = useInvestorImportResolveConflicts();
  const executeMutation = useInvestorImportExecute();

  // Cleanup session on unmount or close
  const handleClose = useCallback(() => {
    if (sessionId && !importResult?.success) {
      cleanupSession(sessionId);
    }
    onClose();
  }, [sessionId, importResult, cleanupSession, onClose]);

  const steps: WizardStep[] = [
    {
      id: 'upload',
      title: 'Upload File',
      description: 'Upload DFF Compliance Excel file',
      icon: <FileText className="w-5 h-5" />,
      completed: validationResult?.is_valid ?? false,
      current: currentStep === 0,
    },
    {
      id: 'funds',
      title: 'Match Funds',
      description: 'Map funds from file to existing funds',
      icon: <Users className="w-5 h-5" />,
      completed:
        fundMatches.length > 0 && fundMatches.some((f) => f.is_matched),
      current: currentStep === 1,
    },
    {
      id: 'conflicts',
      title: 'Resolve Conflicts',
      description: 'Handle existing investor conflicts',
      icon: <AlertCircle className="w-5 h-5" />,
      completed: conflicts.every((c) => c.should_overwrite !== undefined),
      current: currentStep === 2,
    },
    {
      id: 'import',
      title: 'Execute Import',
      description: 'Review and execute the import',
      icon: <CheckCircle className="w-5 h-5" />,
      completed: importResult?.success ?? false,
      current: currentStep === 3,
    },
  ];

  const handleFileUploaded = useCallback(
    (result: ImportValidationResult, sessionId: string) => {
      setValidationResult(result);
      setSessionId(sessionId);
      if (result.is_valid) {
        setCurrentStep(1);
      }
    },
    []
  );

  const handleFundsMatched = useCallback((matches: FundMatchingData[]) => {
    setFundMatches(matches);
  }, []);

  const handleConflictsResolved = useCallback(
    (resolvedConflicts: ExistingInvestorConflict[]) => {
      setConflicts(resolvedConflicts);
    },
    []
  );

  const handleImportCompleted = useCallback((result: ImportResult) => {
    setImportResult(result);
  }, []);

  const canProceedToNext = () => {
    switch (currentStep) {
      case 0:
        return validationResult?.is_valid ?? false;
      case 1:
        return fundMatches.length > 0 && fundMatches.some((f) => f.is_matched);
      case 2:
        return conflicts.every((c) => c.should_overwrite !== undefined);
      case 3:
        return false; // Final step
      default:
        return false;
    }
  };

  const getActionButtonLabel = () => {
    if (isProcessing) {
      switch (currentStep) {
        case 1:
          return 'Saving Fund Matches...';
        case 2:
          return 'Saving Conflict Resolution...';
        case 3:
          return 'Executing Import...';
        default:
          return 'Processing...';
      }
    }

    switch (currentStep) {
      case 0:
        return 'Continue to Fund Matching';
      case 1:
        return 'Continue to Conflict Resolution';
      case 2:
        return 'Continue to Import Execution';
      case 3:
        return 'Execute Import';
      default:
        return 'Next';
    }
  };

  const handleActionButton = async () => {
    if (!canProceedToNext() && currentStep !== 3) return;

    setIsProcessing(true);

    try {
      switch (currentStep) {
        case 0:
          // File upload - just move to next step
          setCurrentStep(1);
          break;

        case 1:
          // Save fund matches
          await updateMatchesMutation.mutateAsync({
            sessionId,
            fundMappings: fundMatches,
          });
          setCurrentStep(2);
          break;

        case 2:
          // Save conflict resolutions
          await resolveConflictsMutation.mutateAsync({
            sessionId,
            conflicts,
          });
          setCurrentStep(3);
          break;

        case 3: {
          // Execute import
          const result = await executeMutation.mutateAsync({
            sessionId,
            fundMappings: fundMatches,
            conflictResolutions: conflicts,
          });
          setImportResult(result);
          break;
        }
      }
    } catch (error) {
      console.error('Error processing step:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <FileUploadStep onFileUploaded={handleFileUploaded} />;
      case 1:
        return (
          <FundMatchingStep
            sessionId={sessionId}
            onFundsMatched={handleFundsMatched}
          />
        );
      case 2:
        return (
          <ConflictResolutionStep
            sessionId={sessionId}
            validationResult={validationResult!}
            fundMatches={fundMatches}
            onConflictsResolved={handleConflictsResolved}
          />
        );
      case 3:
        return (
          <ImportExecutionStep
            sessionId={sessionId}
            validationResult={validationResult!}
            fundMatches={fundMatches}
            conflicts={conflicts}
            importResult={importResult}
            onImportCompleted={handleImportCompleted}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Investor Import Wizard
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={false}
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`
                  flex items-center justify-center w-8 h-8 rounded-full border-2
                  ${getStepCircleClass(step)}
                `}
                >
                  {step.completed ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                <div className="ml-3">
                  <div
                    className={`text-sm font-medium ${getStepTitleClass(step)}`}
                  >
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-500">
                    {step.description}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-gray-400 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[60vh]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Step {currentStep + 1} of {steps.length}
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              {currentStep === steps.length - 1 && importResult ? (
                <Button
                  onClick={handleClose}
                  disabled={false}
                  variant={importResult.success ? 'default' : 'outline'}
                >
                  {importResult.success ? 'Complete' : 'Close'}
                </Button>
              ) : (
                <Button
                  onClick={handleActionButton}
                  disabled={
                    (!canProceedToNext() && currentStep !== 3) || isProcessing
                  }
                >
                  {currentStep === 3 && <Play className="w-4 h-4 mr-2" />}
                  {getActionButtonLabel()}
                  {currentStep < 3 && <ChevronRight className="w-4 h-4 ml-2" />}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
